import React, { useEffect, useRef, useState } from "react";
import { Add, Delete, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

const initialValues = (rows) =>
  rows.map((row) => ({
    yearGroup: row.yearGroup?.id || "",
    subjects: (row.subjects || []).map((subject) => subject.id),
    tier: row.tier || "",
    numberOfLicencesAllowed: row.numberOfLicencesAllowed ?? 0,
    actualNumberOfLicences: row.actualNumberOfLicences ?? null,
  }));

export function LicenseUsageTable({
  initialRows = [],
  subjects = [],
  yearGroups = [],
  licenseOptions = [],
  onSave,
  onChange,
  disabled = false,
  showSaveButton = true,
  showLicencesUsed = true,
  title = "Licence allocation",
  titleVariant = "subtitle2",
}) {
  const [rows, setRows] = useState(() => initialValues(initialRows));
  const [saving, setSaving] = useState(false);
  const initialKey = JSON.stringify(initialRows);
  const lastKey = useRef(initialKey);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  useEffect(() => {
    if (initialKey !== lastKey.current) {
      lastKey.current = initialKey;
      setRows(initialValues(initialRows));
    }
  }, [initialKey, initialRows]);
  const payload = (values) =>
    values.map((row) => ({
      yearGroup: row.yearGroup,
      subjects: row.subjects,
      tier: row.tier,
      numberOfLicencesAllowed: Number(row.numberOfLicencesAllowed),
    }));
  const change = (next) => {
    setRows(next);
    changeRef.current?.(payload(next));
  };
  const update = (index, fields) =>
    change(rows.map((row, i) => (i === index ? { ...row, ...fields } : row)));
  const invalid =
    rows.some(
      (row) =>
        !row.yearGroup ||
        row.numberOfLicencesAllowed === "" ||
        !Number.isInteger(Number(row.numberOfLicencesAllowed)) ||
        Number(row.numberOfLicencesAllowed) < 0,
    ) || new Set(rows.map((row) => row.yearGroup)).size !== rows.length;
  const save = async () => {
    setSaving(true);
    try {
      await onSave?.(payload(rows));
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to save licences", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Accordion variant="outlined">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography
          variant={titleVariant}
          fontWeight="medium"
          sx={{ ml: 1, color: "primary.main" }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Typography>
            Each year group has one licence covering its enabled subjects.
          </Typography>
          {showSaveButton && (
            <Button disabled={disabled || saving || invalid} onClick={save}>
              Save
            </Button>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Year group</TableCell>
                <TableCell>Enabled subjects</TableCell>
                <TableCell>Licence tier</TableCell>
                <TableCell>Licences allowed</TableCell>
                {showLicencesUsed && <TableCell>Licences used</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      select
                      label="Year group"
                      value={row.yearGroup}
                      disabled={disabled || saving}
                      onChange={(event) =>
                        update(index, { yearGroup: event.target.value })
                      }
                    >
                      {yearGroups
                        .filter(
                          (year) =>
                            year.id === row.yearGroup ||
                            !rows.some((item) => item.yearGroup === year.id),
                        )
                        .map((year) => (
                          <MenuItem key={year.id} value={year.id}>
                            Year {year.yearGroup}
                          </MenuItem>
                        ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      options={subjects}
                      value={subjects.filter((subject) =>
                        row.subjects.includes(subject.id),
                      )}
                      getOptionLabel={(subject) => subject.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      disabled={disabled || saving}
                      onChange={(_, values) =>
                        update(index, {
                          subjects: values.map((subject) => subject.id),
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Enabled subjects" />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      label="Licence tier"
                      value={row.tier}
                      disabled={disabled || saving}
                      onChange={(event) =>
                        update(index, { tier: event.target.value })
                      }
                    >
                      <MenuItem value="">School default</MenuItem>
                      {licenseOptions.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {tier}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      label="Licences allowed"
                      type="number"
                      value={row.numberOfLicencesAllowed}
                      inputProps={{ min: 0, step: 1 }}
                      disabled={disabled || saving}
                      onChange={(event) =>
                        update(index, {
                          numberOfLicencesAllowed: event.target.value,
                        })
                      }
                    />
                  </TableCell>
                  {showLicencesUsed && (
                    <TableCell>
                      {row.actualNumberOfLicences ?? "Not available"}
                    </TableCell>
                  )}
                  <TableCell>
                    <IconButton
                      aria-label="Remove year group"
                      disabled={disabled || saving}
                      onClick={() => change(rows.filter((_, i) => i !== index))}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            startIcon={<Add />}
            disabled={disabled || saving || rows.length >= yearGroups.length}
            onClick={() =>
              change([
                ...rows,
                {
                  yearGroup: "",
                  subjects: [],
                  tier: "",
                  numberOfLicencesAllowed: 0,
                  actualNumberOfLicences: null,
                },
              ])
            }
          >
            Add year group
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
